import { useEffect, useMemo, useRef, useState, type Dispatch } from 'react';
import { parseAnimation, type PreviewTemplate } from '@game-platform/pack-animation';
import { useMe } from '../../auth/MeContext';
import { operatorConsole, type PoolOfferConfig } from '../../lib/api/operatorConsole';
import {
  findPack,
  PACK_OPENING_UNSUPPORTED,
  packScopeBlocker,
  packTitle,
  type AnimationScope,
} from './animationScope';
import type { EditorAction, EditorState } from './useAnimationEditor';
import { useSaveStatus } from './useSaveStatus';
import { useTemplateTheme, type DesignSystem } from './useTemplateTheme';

interface Options {
  template: PreviewTemplate;
  /** Who a global save reaches, as the Save button names it: "all packs", or "all packs (Modern)". */
  globalAudience: string;
  packs: PoolOfferConfig[];
  packId: string;
  setPackId: (id: string) => void;
  replacePack: (pack: PoolOfferConfig) => void;
  pinPack: (id: string) => void;
  editor: [EditorState, Dispatch<EditorAction>];
}

const DISCARD_QUESTION = 'Discard unsaved animation changes?';

const globalOf = (designSystem: DesignSystem | null) => parseAnimation(designSystem?.['animation']);

// Where the editor's animation comes from and where Save writes it:
// the template ('global') or the selected pack's own opening ('pack').
export function useScopedAnimation({
  template,
  globalAudience,
  packs,
  packId,
  setPackId,
  replacePack,
  pinPack,
  editor,
}: Options) {
  const [state, dispatch] = editor;
  // What the editor holds now, for a write that answers after further edits
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  });
  const { can } = useMe();
  const [scope, setScope] = useState<AnimationScope>('global');
  // The pack whose opening the editor holds while in pack scope
  const [targetId, setTargetId] = useState('');
  const { busy, message, run } = useSaveStatus();

  const target = scope === 'pack' ? findPack(packs, targetId) : undefined;
  const override = target?.animation ?? null;

  // A (re)loaded theme re-seeds the editor, unless it holds a pack's own opening or edits towards one
  const { designSystem, replaceDesignSystem, loadError } = useTemplateTheme(template, (loaded) => {
    if (scope === 'global' || (!override && !state.dirty)) {
      dispatch({ type: 'loaded', animation: globalOf(loaded) });
    }
  });
  const globalAnimation = useMemo(() => globalOf(designSystem), [designSystem]);

  useEffect(() => pinPack(scope === 'pack' ? targetId : ''), [scope, targetId, pinPack]);

  // The edited pack left the list and a re-read selected another one
  useEffect(() => {
    if (scope !== 'pack' || packId === targetId) return;
    setScope('global');
    setTargetId('');
    dispatch({ type: 'loaded', animation: globalAnimation });
  }, [scope, packId, targetId, globalAnimation, dispatch]);

  const switchTo = (nextScope: AnimationScope, nextPackId: string) => {
    const same = nextScope === scope && (nextScope === 'global' || nextPackId === targetId);
    if (!same && state.dirty && !window.confirm(DISCARD_QUESTION)) return;
    setScope(nextScope);
    setTargetId(nextScope === 'pack' ? nextPackId : '');
    setPackId(nextPackId);
    if (same) return;
    const own = nextScope === 'pack' ? findPack(packs, nextPackId)?.animation : null;
    dispatch({ type: 'loaded', animation: own ? parseAnimation(own) : globalAnimation });
  };

  const blocker = packScopeBlocker(can('offers'), findPack(packs, packId));

  const changeScope = (next: AnimationScope) => {
    if (next === 'pack' && blocker) return;
    switchTo(next, packId);
  };

  // In pack scope the selector picks what is edited; the lab demo has no opening of its own
  const changePack = (id: string) => {
    if (scope === 'global') setPackId(id);
    else switchTo(findPack(packs, id) ? 'pack' : 'global', id);
  };

  const save = () =>
    run(async () => {
      if (scope === 'pack') {
        if (!target) throw new Error('Select a pack');
        const sent = state.animation;
        const updated = await operatorConsole.patchPoolOfferConfig(target.pool_offer_id, { animation: sent });
        if (updated.animation == null) throw new Error(PACK_OPENING_UNSUPPORTED);
        replacePack(updated);
        dispatch({ type: 'saved', animation: sent });
        return `Saved for ${packTitle(updated)}`;
      }
      // Re-read before writing: /themes may have saved palette changes since this page loaded
      const sent = state.animation;
      const fresh = await operatorConsole.getTemplateTheme(template);
      const next = { ...fresh.design_system, animation: sent };
      await operatorConsole.putTemplateTheme(template, next);
      replaceDesignSystem(next);
      dispatch({ type: 'saved', animation: sent });
      return `Saved for ${globalAudience}`;
    });

  const revertToGlobal = () => {
    if (!target) return;
    const lost = state.dirty ? ' Unsaved changes will be lost.' : '';
    if (!window.confirm(`${packTitle(target)} will play the global animation again.${lost}`)) return;
    const before = state.animation;
    void run(async () => {
      const updated = await operatorConsole.patchPoolOfferConfig(target.pool_offer_id, { animation: null });
      replacePack(updated);
      // Edits made while the write was in flight are kept
      if (latest.current.animation === before) dispatch({ type: 'loaded', animation: globalAnimation });
      return `${packTitle(updated)} uses the global animation`;
    });
  };

  return {
    designSystem,
    loadError,
    scope,
    changeScope,
    changePack,
    packScopeBlocker: blocker,
    editedPack: target,
    // Inheriting the global one, Save gives the pack its own copy of it
    canSave: !busy && (state.dirty || (Boolean(target) && !override)),
    busy,
    message,
    save,
    revertToGlobal,
  };
}
